import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  buildThreads,
  dueDeliveries,
  nextDeliveryAt,
  type HydratedStory,
  type ThreadMessage,
  type ThreadView,
} from 'magical-trails';

import { stampDeliveries } from '../../store/trail';

export interface PhoneArrival {
  id: string;
  threadKey: string;
  message: ThreadMessage;
}

export interface PhoneMessagesState {
  threads: ThreadView[];
  /** The newest message to land this session that nobody has looked at. */
  arrival: PhoneArrival | null;
  dismissArrival: () => void;
}

// setTimeout takes a 32-bit delay; a longer one fires at once.
const MAX_TIMER_MS = 2_000_000_000;

/**
 * The messenger's engine: stamps messages as their points unlock, wakes up
 * when a delayed one is due, and notices arrivals so the shell can announce
 * them. All of the actual reading is `magical-trails`' pure thread logic;
 * this only supplies the clock and the store.
 */
export function usePhoneMessages(
  story: HydratedStory | undefined,
  /** The thread on screen, if any: its arrivals need no announcement. */
  openThreadKey: string | null
): PhoneMessagesState {
  const dispatch = useDispatch();
  const [now, setNow] = useState(() => Date.now());
  const [arrival, setArrival] = useState<PhoneArrival | null>(null);

  const points = story?.storyPoints;
  const settings = story?.config;

  // Stamp whatever has unlocked since the last look. Idempotent twice over:
  // dueDeliveries skips stamped points and the reducer never re-stamps.
  useEffect(() => {
    if (!points) return;
    const due = dueDeliveries(points, settings, Date.now());
    if (due.length > 0) {
      dispatch(stampDeliveries(due));
      setNow(Date.now());
    }
  }, [points, settings, dispatch]);

  // One timer, for the soonest message still inside its delay.
  useEffect(() => {
    if (!points) return;
    const at = nextDeliveryAt(points, settings, now);
    if (at === null) return;
    const timer = setTimeout(
      () => setNow(Date.now()),
      Math.min(Math.max(at - Date.now(), 0) + 50, MAX_TIMER_MS)
    );
    return () => clearTimeout(timer);
  }, [points, settings, now]);

  // A phone left in a pocket throttles timers: catch up on return.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setNow(Date.now());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const threads = useMemo(
    () => (points ? buildThreads(points, settings, now) : []),
    [points, settings, now]
  );

  // Announce a message the first time it shows up as delivered — but not the
  // backlog a returning player already has (everything present on the first
  // pass that was stamped before this session began).
  const sessionStart = useRef(Date.now());
  const announced = useRef<Set<string> | null>(null);
  const storyCode = story?.code;
  useEffect(() => {
    announced.current = null;
    setArrival(null);
  }, [storyCode]);

  useEffect(() => {
    const firstPass = announced.current === null;
    const seen = announced.current ?? new Set<string>();
    announced.current = seen;
    let newest: PhoneArrival | null = null;
    for (const thread of threads) {
      for (const message of thread.messages) {
        const id = message.point.code;
        if (seen.has(id)) continue;
        seen.add(id);
        const fromBefore =
          firstPass && message.deliveredAt < sessionStart.current - 1000;
        if (fromBefore || !message.unread || thread.key === openThreadKey) {
          continue;
        }
        if (!newest || message.deliveredAt >= newest.message.deliveredAt) {
          newest = { id, threadKey: thread.key, message };
        }
      }
    }
    if (newest) setArrival(newest);
  }, [threads, openThreadKey]);

  // Opening the thread answers the banner.
  useEffect(() => {
    if (arrival && arrival.threadKey === openThreadKey) setArrival(null);
  }, [arrival, openThreadKey]);

  return { threads, arrival, dismissArrival: () => setArrival(null) };
}

export default usePhoneMessages;
